"use client";

import * as Sentry from "@sentry/nextjs";
import { getFileUploadToken } from "api/getFileUploadToken";
import ky, { type Progress } from "ky";
import { getKyErrorDetails } from "./helpers/getKyErrorDetails";
import { supportsRequestStreams } from "./helpers/supportsRequestStreams";

const UPLOAD_TIMEOUT_MS = 20 * 60 * 1000;

export interface UploadFileResponse {
    fileid: string;
}

export interface UploadFileResult {
    isSuccess: boolean;
    data: UploadFileResponse | null;
    error: Error | null;
}

export interface UploadFileClientOptions {
    onUploadProgress?: (progress: Progress, chunk: Uint8Array) => void;
}

/**
 * Error shape used by the XHR upload path. Mirrors enough of `ky`'s `HTTPError`
 * surface (status, response payload) so that downstream consumers like
 * `handleFileUploadError` work uniformly across browsers.
 */
class UploadHttpError extends Error {
    public readonly name = "HTTPError";

    constructor(
        message: string,
        public readonly status: number,
        public readonly statusText: string,
        public readonly url: string,
        public readonly data: unknown,
    ) {
        super(message);
    }
}

function uploadViaKy(
    apiUrl: string,
    formData: FormData,
    authorization: string,
    onUploadProgress?: UploadFileClientOptions["onUploadProgress"],
): Promise<UploadFileResponse> {
    return ky
        .post(`${apiUrl}/forms/files`, {
            body: formData,
            headers: { Authorization: authorization },
            timeout: UPLOAD_TIMEOUT_MS,
            onUploadProgress,
        })
        .json<UploadFileResponse>();
}

/**
 * XHR-based upload used when the runtime can't construct a streaming `Request`
 * (notably Safari, which ignores the `duplex` option). `XMLHttpRequest` exposes
 * `upload.onprogress` everywhere, so we can still surface real progress.
 */
function uploadViaXhr(
    apiUrl: string,
    formData: FormData,
    authorization: string,
    onUploadProgress?: UploadFileClientOptions["onUploadProgress"],
): Promise<UploadFileResponse> {
    return new Promise((resolve, reject) => {
        const url = `${apiUrl}/forms/files`;
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url);
        xhr.setRequestHeader("Authorization", authorization);
        xhr.timeout = UPLOAD_TIMEOUT_MS;
        xhr.responseType = "text";

        if (onUploadProgress) {
            xhr.upload.onprogress = (event) => {
                const totalBytes = event.lengthComputable ? event.total : 0;
                const transferredBytes = event.loaded;
                const percent = totalBytes > 0 ? transferredBytes / totalBytes : 0;
                onUploadProgress({ percent, transferredBytes, totalBytes }, new Uint8Array());
            };
        }

        xhr.onload = () => {
            const text = xhr.responseText;
            let body: unknown = text;
            if (text) {
                try {
                    body = JSON.parse(text);
                } catch {
                    // Non-JSON response (e.g. HTML error page); keep raw text.
                }
            }

            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(body as UploadFileResponse);
                return;
            }

            reject(new UploadHttpError(`Upload failed with status ${xhr.status}`, xhr.status, xhr.statusText, url, body));
        };
        xhr.onerror = () => reject(new Error("Network error during upload"));
        xhr.ontimeout = () => reject(new Error("Upload timeout"));
        xhr.onabort = () => reject(new Error("Upload aborted"));

        xhr.send(formData);
    });
}

/**
 * Upload file directly from browser to API.
 *
 * Strategy:
 * - When the browser supports streaming request bodies (Chrome/Firefox), we use
 *   `ky` which uses Fetch with `duplex: "half"` to expose `onUploadProgress`.
 * - Otherwise (Safari), we fall back to `XMLHttpRequest` so we can still emit
 *   real upload progress via `upload.onprogress`.
 */
export async function uploadFileClient(file: File, options?: UploadFileClientOptions): Promise<UploadFileResult> {
    try {
        const tokenData = await getFileUploadToken();

        const formData = new FormData();
        formData.append("file", file);

        const apiUrl = process.env.NEXT_PUBLIC_API_FORMS_URL;
        if (!apiUrl) {
            throw new Error("NEXT_PUBLIC_API_FORMS_URL is not defined");
        }

        const response = await ky
            .post(`${apiUrl}/forms/files`, {
                body: formData,
                headers: {
                    Authorization: `${tokenData.token_type} ${tokenData.access_token}`,
                },
                timeout: UPLOAD_TIMEOUT_MS,
                ...(options?.onUploadProgress ? { onUploadProgress: options.onUploadProgress } : {}),
            })
            .json<UploadFileResponse>();

        return {
            isSuccess: true,
            data: response,
            error: null,
        };
    } catch (error) {
        console.error("File upload failed:", error);
        const errorDetails = await getKyErrorDetails(error);
        Sentry.withScope((scope) => {
            scope.setTag("feature", "client-file-upload");
            scope.setExtras({
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
                errorDetails,
            });
            Sentry.captureException(error);
        });

        return {
            isSuccess: false,
            data: null,
            error: error instanceof Error ? error : new Error("Unknown error during file upload"),
        };
    }
}
